import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Component, inject, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import {
    DisplayValue,
    InviteUserDialogResult,
    Organization,
    CheckEmailStatusResponse,
    InvitationDocument,
    ListInvitationsResponse,
    ResendInvitationResponse,
    RevokeInvitationResponse,
    DeleteInvitationResponse,
    InvitationStatus,
} from '@newgenus/common';
import {
    OrgItemSelectorDialogComponent,
    ItemSelectorDialogResult,
} from '../org-item-selector-dialog/org-item-selector-dialog';
import { MaterialModule } from '../../../material.module';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgFor, NgIf, TitleCasePipe } from '@angular/common';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Data passed to the invite user dialog.
 */
export interface InviteUserDialogData {
    organization: Organization;
    apiBaseUrl?: string;
    /** Optional callback to check email status against Firebase Auth & org membership */
    checkEmailStatus?: (email: string, orgKey: string) => Observable<CheckEmailStatusResponse>;
    /** Callback to load sent invitations for the organization */
    getOrgInvitations?: (orgKey: string, status?: InvitationStatus) => Observable<ListInvitationsResponse>;
    /** Callback to resend a pending invitation */
    resendInvitation?: (invitationKey: string) => Observable<ResendInvitationResponse>;
    /** Callback to revoke (cancel) a pending invitation */
    revokeInvitation?: (invitationKey: string) => Observable<RevokeInvitationResponse>;
    /** Callback to permanently delete an invitation record */
    deleteInvitation?: (invitationKey: string) => Observable<DeleteInvitationResponse>;
}

/**
 * Represents a single user to be invited, with optional position/hat assignments.
 */
interface InviteeEntry {
    email: string;
    positionKeys: string[];
    hatKeys: string[];
    message?: string;
}

@Component({
    selector: 'shared-org-invite-user-dialog',
    imports: [MaterialModule, ReactiveFormsModule, NgFor, NgIf, MatDialogModule, TitleCasePipe],
    template: `
        <h2 mat-dialog-title class="dialog-title">
            <img src="/assets/icons/branding/newgenus/icon-96x96.png" alt="Newgenus" class="newgenus-dialog-logo" />
            Invite users to {{ orgName }}
            <button mat-icon-button mat-dialog-close class="close-button">
                <mat-icon>close</mat-icon>
            </button>
        </h2>

        <mat-tab-group
            class="invite-tabs"
            [(selectedIndex)]="activeTabIndex"
            (selectedIndexChange)="onTabChange($event)"
        >
            <!-- ══════════════════════════════════════════════ -->
            <!-- TAB 1: New Invitation                         -->
            <!-- ══════════════════════════════════════════════ -->
            <mat-tab>
                <ng-template mat-tab-label>
                    <mat-icon class="tab-icon">person_add</mat-icon>
                    New Invitation
                </ng-template>

                <mat-dialog-content class="dialog-body">
                    <div class="invite-container">
                        <!-- ============================================ -->
                        <!-- Section 1: Email Entry -->
                        <!-- ============================================ -->
                        <div class="section">
                            <div class="section-header">
                                <mat-icon>email</mat-icon>
                                <span>Email Address</span>
                            </div>

                            <form [formGroup]="emailForm" class="email-form">
                                <mat-form-field appearance="outline" class="w-100">
                                    <mat-label>Email</mat-label>
                                    <input
                                        matInput
                                        formControlName="email"
                                        type="email"
                                        placeholder="Enter email address"
                                        (keydown.enter)="$event.preventDefault(); addUserToList()"
                                        (blur)="onEmailBlur()"
                                    />
                                    <mat-icon matSuffix *ngIf="!emailChecking && !emailStatus">email</mat-icon>
                                    <mat-icon matSuffix *ngIf="emailChecking" class="spin-icon">sync</mat-icon>
                                    <mat-icon
                                        matSuffix
                                        *ngIf="!emailChecking && emailStatus?.existsInOrg"
                                        class="status-icon-warn"
                                        >group</mat-icon
                                    >
                                    <mat-icon
                                        matSuffix
                                        *ngIf="
                                            !emailChecking &&
                                            emailStatus &&
                                            !emailStatus.existsInOrg &&
                                            emailStatus.existsInAuth
                                        "
                                        class="status-icon-ok"
                                        >check_circle</mat-icon
                                    >
                                    <mat-icon
                                        matSuffix
                                        *ngIf="
                                            !emailChecking &&
                                            emailStatus &&
                                            !emailStatus.existsInOrg &&
                                            !emailStatus.existsInAuth
                                        "
                                        class="status-icon-new"
                                        >person_add</mat-icon
                                    >
                                    <mat-error *ngIf="emailForm.get('email')?.hasError('required')"
                                        >Email is required</mat-error
                                    >
                                    <mat-error *ngIf="emailForm.get('email')?.hasError('email')"
                                        >Invalid email</mat-error
                                    >
                                </mat-form-field>
                            </form>

                            <!-- Email Status Indicators -->
                            <div class="email-status" *ngIf="emailStatus && !emailChecking">
                                <div *ngIf="emailStatus.existsInOrg" class="status-badge status-org-member">
                                    <mat-icon>warning</mat-icon>
                                    <span>Already a member of this organization</span>
                                </div>
                                <div
                                    *ngIf="emailStatus.hasPendingInvitation && !emailStatus.existsInOrg"
                                    class="status-badge status-pending"
                                >
                                    <mat-icon>schedule</mat-icon>
                                    <span>Has a pending invitation</span>
                                </div>
                                <div
                                    *ngIf="emailStatus.existsInAuth && !emailStatus.existsInOrg"
                                    class="status-badge status-existing"
                                >
                                    <mat-icon>check_circle</mat-icon>
                                    <span
                                        >Existing user{{
                                            emailStatus.displayName ? ': ' + emailStatus.displayName : ''
                                        }}</span
                                    >
                                </div>
                                <div
                                    *ngIf="!emailStatus.existsInAuth && !emailStatus.existsInOrg"
                                    class="status-badge status-new"
                                >
                                    <mat-icon>person_add</mat-icon>
                                    <span>New user &mdash; will receive signup link</span>
                                </div>
                            </div>
                            <div class="email-status" *ngIf="emailChecking">
                                <div class="status-badge status-checking">
                                    <mat-icon class="spin-icon">sync</mat-icon>
                                    <span>Checking email...</span>
                                </div>
                            </div>
                        </div>

                        <!-- ============================================ -->
                        <!-- Section 2: Positions & Hats (via Browser) -->
                        <!-- ============================================ -->
                        <div class="section">
                            <div class="section-header">
                                <mat-icon class="icon-positions">account_tree</mat-icon>
                                <span>Org Parts & Hats</span>
                                <span class="optional-badge">Optional</span>
                            </div>

                            <p class="section-hint">
                                Select org parts from the organization tree. Hats on those org parts will become
                                available for selection.
                            </p>

                            <button mat-stroked-button class="browse-btn" (click)="openPositionHatSelector()">
                                <mat-icon>search</mat-icon> Browse Org Parts & Hats
                            </button>

                            <!-- Selected Positions -->
                            <div class="selected-group" *ngIf="selectedPositions.length > 0">
                                <span class="group-label group-positions">
                                    <mat-icon>account_tree</mat-icon> Org Parts
                                </span>
                                <div class="selected-chips">
                                    <span class="selected-chip chip-position" *ngFor="let item of selectedPositions">
                                        {{ item.display }}
                                        <mat-icon
                                            class="chip-remove"
                                            (click)="removePosition(item.value); $event.stopPropagation()"
                                            >close</mat-icon
                                        >
                                    </span>
                                </div>
                            </div>

                            <!-- Selected Hats -->
                            <div class="selected-group" *ngIf="selectedHats.length > 0">
                                <span class="group-label group-hats"> <mat-icon>badge</mat-icon> Hats </span>
                                <div class="selected-chips">
                                    <span class="selected-chip chip-hat" *ngFor="let item of selectedHats">
                                        {{ item.display }}
                                        <mat-icon
                                            class="chip-remove"
                                            (click)="removeHat(item.value); $event.stopPropagation()"
                                            >close</mat-icon
                                        >
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- ============================================ -->
                        <!-- Section 3: Personal Message (Optional) -->
                        <!-- ============================================ -->
                        <div class="section">
                            <div class="section-header">
                                <mat-icon>message</mat-icon>
                                <span>Personal Message</span>
                                <span class="optional-badge">Optional</span>
                            </div>

                            <mat-form-field appearance="outline" class="w-100">
                                <mat-label>Add a personal message to the invitation</mat-label>
                                <textarea
                                    matInput
                                    [formControl]="messageControl"
                                    rows="2"
                                    placeholder="e.g., Welcome to the team! Looking forward to working with you."
                                ></textarea>
                            </mat-form-field>
                        </div>

                        <!-- ============================================ -->
                        <!-- Add to List Button -->
                        <!-- ============================================ -->
                        <div class="add-button-row">
                            <button
                                mat-raised-button
                                color="primary"
                                [disabled]="emailForm.invalid || !emailForm.get('email')?.value"
                                (click)="addUserToList()"
                            >
                                <mat-icon>person_add</mat-icon> Add to Invitation List
                            </button>
                        </div>

                        <!-- ============================================ -->
                        <!-- Section 4: Invitation List -->
                        <!-- ============================================ -->
                        <div class="section" *ngIf="invitees.length > 0">
                            <div class="section-header">
                                <mat-icon>group</mat-icon>
                                <span>Invitation List ({{ invitees.length }})</span>
                            </div>

                            <div class="invitee-list">
                                <div *ngFor="let invitee of invitees; let i = index" class="invitee-card">
                                    <div class="invitee-header">
                                        <div class="invitee-email">
                                            <mat-icon>person</mat-icon>
                                            <strong>{{ invitee.email }}</strong>
                                        </div>
                                        <button
                                            mat-icon-button
                                            color="warn"
                                            (click)="removeFromList(i)"
                                            matTooltip="Remove"
                                        >
                                            <mat-icon>remove_circle</mat-icon>
                                        </button>
                                    </div>

                                    <div
                                        class="invitee-details"
                                        *ngIf="invitee.positionKeys.length > 0 || invitee.hatKeys.length > 0"
                                    >
                                        <div *ngIf="invitee.positionKeys.length > 0" class="detail-row">
                                            <mat-icon class="detail-icon icon-positions">account_tree</mat-icon>
                                            <span class="detail-chips">
                                                <span
                                                    class="mini-chip chip-position"
                                                    *ngFor="let pk of invitee.positionKeys"
                                                >
                                                    {{ getPositionName(pk) }}
                                                </span>
                                            </span>
                                        </div>
                                        <div *ngIf="invitee.hatKeys.length > 0" class="detail-row">
                                            <mat-icon class="detail-icon icon-hats">badge</mat-icon>
                                            <span class="detail-chips">
                                                <span class="mini-chip chip-hat" *ngFor="let hk of invitee.hatKeys">
                                                    {{ getHatName(hk) }}
                                                </span>
                                            </span>
                                        </div>
                                    </div>

                                    <div class="invitee-message" *ngIf="invitee.message">
                                        <mat-icon class="detail-icon">message</mat-icon>
                                        <em>"{{ invitee.message }}"</em>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </mat-dialog-content>
            </mat-tab>

            <!-- ══════════════════════════════════════════════ -->
            <!-- TAB 2: Sent Invitations                       -->
            <!-- ══════════════════════════════════════════════ -->
            <mat-tab>
                <ng-template mat-tab-label>
                    <mat-icon class="tab-icon">history</mat-icon>
                    Sent Invitations
                    <span class="tab-badge" *ngIf="sentInvitations.length > 0">{{ sentInvitations.length }}</span>
                </ng-template>

                <div class="sent-invitations-container">
                    <!-- Loading Spinner -->
                    <div class="loading-container" *ngIf="sentLoading">
                        <mat-spinner diameter="40"></mat-spinner>
                        <span class="loading-text">Loading invitations...</span>
                    </div>

                    <!-- Drift summary banner (Phase B.7) -->
                    <div class="drift-summary" *ngIf="!sentLoading && sentInvitations.length > 0">
                        <span class="drift-total">{{ sentInvitations.length }} invited</span>
                        <span class="drift-arrow">→</span>
                        <span class="drift-segment drift-accepted">
                            <strong>{{ countByStatus('accepted') }}</strong> accepted
                        </span>
                        <span class="drift-dot">·</span>
                        <span class="drift-segment drift-pending">
                            <strong>{{ countByStatus('pending') }}</strong> pending
                        </span>
                        <span class="drift-dot">·</span>
                        <span class="drift-segment drift-declined">
                            <strong>{{ countByStatus('declined') }}</strong> declined
                        </span>
                        <span class="drift-dot" *ngIf="countByStatus('expired') + countByStatus('cancelled') > 0"
                            >·</span
                        >
                        <span
                            class="drift-segment drift-other"
                            *ngIf="countByStatus('expired') + countByStatus('cancelled') > 0"
                        >
                            <strong>{{ countByStatus('expired') + countByStatus('cancelled') }}</strong>
                            expired/cancelled
                        </span>
                    </div>

                    <!-- Status filter chips -->
                    <div class="filter-row" *ngIf="!sentLoading">
                        <span class="filter-label">Filter:</span>
                        <button
                            mat-stroked-button
                            class="filter-chip"
                            [class.filter-active]="!sentStatusFilter"
                            (click)="filterSentInvitations(undefined)"
                        >
                            All ({{ sentInvitations.length }})
                        </button>
                        <button
                            mat-stroked-button
                            class="filter-chip filter-pending"
                            [class.filter-active]="sentStatusFilter === 'pending'"
                            (click)="filterSentInvitations('pending')"
                        >
                            Pending ({{ countByStatus('pending') }})
                        </button>
                        <button
                            mat-stroked-button
                            class="filter-chip filter-accepted"
                            [class.filter-active]="sentStatusFilter === 'accepted'"
                            (click)="filterSentInvitations('accepted')"
                        >
                            Accepted ({{ countByStatus('accepted') }})
                        </button>
                        <button
                            mat-stroked-button
                            class="filter-chip filter-declined"
                            [class.filter-active]="sentStatusFilter === 'declined'"
                            (click)="filterSentInvitations('declined')"
                        >
                            Declined ({{ countByStatus('declined') }})
                        </button>
                        <button
                            mat-stroked-button
                            class="filter-chip filter-expired-btn"
                            [class.filter-active]="sentStatusFilter === 'expired'"
                            (click)="filterSentInvitations('expired')"
                        >
                            Expired ({{ countByStatus('expired') }})
                        </button>
                        <button
                            mat-stroked-button
                            class="filter-chip filter-cancelled"
                            [class.filter-active]="sentStatusFilter === 'cancelled'"
                            (click)="filterSentInvitations('cancelled')"
                        >
                            Cancelled ({{ countByStatus('cancelled') }})
                        </button>

                        <button
                            mat-icon-button
                            class="refresh-btn"
                            matTooltip="Refresh"
                            (click)="loadSentInvitations()"
                        >
                            <mat-icon>refresh</mat-icon>
                        </button>
                    </div>

                    <!-- Empty state -->
                    <div class="empty-state" *ngIf="!sentLoading && filteredInvitations.length === 0">
                        <mat-icon class="empty-icon">mail_outline</mat-icon>
                        <p *ngIf="sentInvitations.length === 0">
                            No invitations have been sent for this organization yet.
                        </p>
                        <p *ngIf="sentInvitations.length > 0">No invitations match the selected filter.</p>
                    </div>

                    <!-- Invitation List -->
                    <div class="sent-list" *ngIf="!sentLoading && filteredInvitations.length > 0">
                        <div class="sent-card" *ngFor="let inv of filteredInvitations; trackBy: trackByKey">
                            <div class="sent-card-header">
                                <div class="sent-email-row">
                                    <mat-icon class="sent-email-icon">person</mat-icon>
                                    <span class="sent-email">{{ inv.email }}</span>
                                    <span
                                        class="invitation-type-chip"
                                        [class.type-existing]="inv.type === 'existing_user'"
                                        [class.type-new]="inv.type === 'new_user'"
                                    >
                                        {{ inv.type === 'existing_user' ? 'Existing User' : 'New User' }}
                                    </span>
                                </div>
                                <span
                                    class="sent-status-chip"
                                    [class.status-chip-pending]="inv.status === 'pending'"
                                    [class.status-chip-accepted]="inv.status === 'accepted'"
                                    [class.status-chip-declined]="inv.status === 'declined'"
                                    [class.status-chip-expired]="inv.status === 'expired'"
                                    [class.status-chip-cancelled]="inv.status === 'cancelled'"
                                >
                                    <mat-icon class="status-chip-icon">{{ getStatusIcon(inv.status) }}</mat-icon>
                                    {{ inv.status | titlecase }}
                                </span>
                            </div>

                            <div class="sent-card-details">
                                <div class="sent-detail" *ngIf="inv.inviterName">
                                    <mat-icon class="detail-icon">person_outline</mat-icon>
                                    <span
                                        >Invited by <strong>{{ inv.inviterName }}</strong></span
                                    >
                                </div>
                                <div class="sent-detail">
                                    <mat-icon class="detail-icon">schedule</mat-icon>
                                    <span>Sent: {{ formatTimestamp(inv.sentOn) }}</span>
                                </div>
                                <div class="sent-detail" *ngIf="inv.expiresOn">
                                    <mat-icon class="detail-icon">event</mat-icon>
                                    <span>Expires: {{ formatTimestamp(inv.expiresOn) }}</span>
                                </div>
                                <div class="sent-detail" *ngIf="inv.positions && inv.positions.length > 0">
                                    <mat-icon class="detail-icon icon-positions">account_tree</mat-icon>
                                    <span class="detail-chips">
                                        <span class="mini-chip chip-position" *ngFor="let pos of inv.positions">{{
                                            pos.name
                                        }}</span>
                                    </span>
                                </div>
                                <div class="sent-detail" *ngIf="inv.hats && inv.hats.length > 0">
                                    <mat-icon class="detail-icon icon-hats">badge</mat-icon>
                                    <span class="detail-chips">
                                        <span class="mini-chip chip-hat" *ngFor="let hat of inv.hats">{{
                                            hat.name
                                        }}</span>
                                    </span>
                                </div>
                                <div class="sent-detail" *ngIf="inv.message">
                                    <mat-icon class="detail-icon">message</mat-icon>
                                    <em>"{{ inv.message }}"</em>
                                </div>
                                <div class="sent-detail email-delivery-status" *ngIf="inv.emailSent !== undefined">
                                    <mat-icon
                                        class="detail-icon"
                                        [class.email-sent]="inv.emailSent"
                                        [class.email-failed]="!inv.emailSent"
                                    >
                                        {{ inv.emailSent ? 'mark_email_read' : 'unsubscribe' }}
                                    </mat-icon>
                                    <span
                                        [class.email-sent-text]="inv.emailSent"
                                        [class.email-failed-text]="!inv.emailSent"
                                    >
                                        {{ inv.emailSent ? 'Email sent' : 'Email delivery failed' }}
                                    </span>
                                </div>
                            </div>

                            <!-- Actions for pending invitations -->
                            <div class="sent-card-actions" *ngIf="inv.status === 'pending'">
                                <button
                                    mat-stroked-button
                                    class="action-resend"
                                    [disabled]="actionLoadingKeys.has(inv.key)"
                                    matTooltip="Resend invitation email with a new link"
                                    (click)="resendInvitationAction(inv)"
                                >
                                    <mat-icon>send</mat-icon>
                                    Resend
                                </button>
                                <button
                                    mat-stroked-button
                                    class="action-revoke"
                                    [disabled]="actionLoadingKeys.has(inv.key)"
                                    matTooltip="Cancel this invitation"
                                    (click)="revokeInvitationAction(inv)"
                                >
                                    <mat-icon>block</mat-icon>
                                    Revoke
                                </button>
                                <mat-spinner
                                    diameter="18"
                                    *ngIf="actionLoadingKeys.has(inv.key)"
                                    class="action-spinner"
                                ></mat-spinner>
                            </div>

                            <!-- Delete action for non-pending invitations -->
                            <div class="sent-card-actions" *ngIf="inv.status !== 'pending' && data.deleteInvitation">
                                <button
                                    mat-stroked-button
                                    class="action-delete"
                                    [disabled]="actionLoadingKeys.has(inv.key)"
                                    matTooltip="Permanently remove this invitation record"
                                    (click)="deleteInvitationAction(inv)"
                                >
                                    <mat-icon>delete_outline</mat-icon>
                                    Delete
                                </button>
                                <mat-spinner
                                    diameter="18"
                                    *ngIf="actionLoadingKeys.has(inv.key)"
                                    class="action-spinner"
                                ></mat-spinner>
                            </div>
                        </div>
                    </div>
                </div>
            </mat-tab>
        </mat-tab-group>

        <mat-dialog-actions align="end" class="dialog-footer" *ngIf="activeTabIndex === 0">
            <button mat-button mat-dialog-close color="warn">Cancel</button>
            <button mat-raised-button color="primary" [disabled]="invitees.length === 0" (click)="sendInvitations()">
                <mat-icon>forward_to_inbox</mat-icon>
                Send {{ invitees.length }} Invitation{{ invitees.length !== 1 ? 's' : '' }}
            </button>
        </mat-dialog-actions>

        <mat-dialog-actions align="end" class="dialog-footer" *ngIf="activeTabIndex === 1">
            <button mat-button mat-dialog-close color="warn">Close</button>
        </mat-dialog-actions>
    `,
    styles: [
        `
            /* ═══════════════ Dark Theme Dialog ═══════════════ */

            :host {
                display: flex;
                flex-direction: column;
                height: 100%;
                overflow: hidden;
            }

            ::ng-deep .mat-mdc-dialog-surface {
                overflow: hidden !important;
            }

            .dialog-title {
                display: flex;
                align-items: center;
                gap: 10px;
                margin: 0;
                padding: 16px 24px;
                font-size: 18px;
                font-weight: 500;
                border-bottom: 1px solid rgba(255, 255, 255, 0.1);

                .newgenus-dialog-logo {
                    width: 28px;
                    height: 28px;
                    border-radius: 4px;
                }

                .title-icon {
                    color: #64b5f6;
                }

                .close-button {
                    margin-left: auto;
                }
            }

            .dialog-body {
                flex: 1;
                overflow-y: auto;
                padding: 0 !important;
                max-height: unset !important;
            }

            .dialog-footer {
                border-top: 1px solid rgba(255, 255, 255, 0.1);
                padding: 12px 24px;
            }

            .invite-container {
                padding: 16px 24px;
                display: flex;
                flex-direction: column;
                gap: 16px;
            }

            .section {
                background: rgba(255, 255, 255, 0.04);
                border-radius: 8px;
                padding: 16px;
                border: 1px solid rgba(255, 255, 255, 0.08);
            }

            .section-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 12px;
                font-size: 14px;
                font-weight: 500;
                color: rgba(255, 255, 255, 0.87);

                mat-icon {
                    font-size: 20px;
                    width: 20px;
                    height: 20px;
                    color: rgba(255, 255, 255, 0.54);
                }
            }

            .optional-badge {
                font-size: 11px;
                color: rgba(255, 255, 255, 0.4);
                font-weight: 400;
                padding: 2px 8px;
                background: rgba(255, 255, 255, 0.06);
                border-radius: 10px;
                margin-left: auto;
            }

            .section-hint {
                font-size: 12px;
                color: rgba(255, 255, 255, 0.5);
                margin: 0 0 12px 0;
                line-height: 1.4;
            }

            .email-form {
                display: flex;
                gap: 8px;
                align-items: flex-start;
            }

            .browse-btn {
                display: flex;
                align-items: center;
                gap: 6px;
            }

            /* ─── Selected Groups ─── */

            .selected-group {
                margin-top: 12px;
            }

            .group-label {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 12px;
                color: rgba(255, 255, 255, 0.5);
                margin-bottom: 6px;
                text-transform: uppercase;
                letter-spacing: 0.5px;

                mat-icon {
                    font-size: 14px;
                    width: 14px;
                    height: 14px;
                }
            }

            .selected-chips {
                display: flex;
                flex-wrap: wrap;
                gap: 6px;
            }

            .selected-chip {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                padding: 4px 10px;
                border-radius: 16px;
                font-size: 12px;
                cursor: default;

                .chip-remove {
                    font-size: 14px;
                    width: 14px;
                    height: 14px;
                    cursor: pointer;
                    opacity: 0.7;
                    &:hover {
                        opacity: 1;
                    }
                }
            }

            .chip-position {
                background: color-mix(in srgb, var(--entity-color-positions, #388e3c) 20%, transparent);
                color: var(--entity-color-positions, #388e3c);
                border: 1px solid color-mix(in srgb, var(--entity-color-positions, #388e3c) 30%, transparent);
            }

            .chip-hat {
                background: color-mix(in srgb, var(--entity-color-hats, #1093ad) 20%, transparent);
                color: var(--entity-color-hats, #1093ad);
                border: 1px solid color-mix(in srgb, var(--entity-color-hats, #1093ad) 30%, transparent);
            }

            /* ─── Add Button ─── */

            .add-button-row {
                display: flex;
                justify-content: center;
            }

            /* ─── Invitee List ─── */

            .invitee-list {
                display: flex;
                flex-direction: column;
                gap: 8px;
            }

            .invitee-card {
                background: rgba(255, 255, 255, 0.03);
                border-radius: 8px;
                padding: 12px 16px;
                border: 1px solid rgba(255, 255, 255, 0.08);
            }

            .invitee-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
            }

            .invitee-email {
                display: flex;
                align-items: center;
                gap: 8px;

                mat-icon {
                    color: rgba(255, 255, 255, 0.5);
                    font-size: 18px;
                    width: 18px;
                    height: 18px;
                }
            }

            .invitee-details {
                margin-top: 8px;
                padding-left: 26px;
            }

            .detail-row {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 4px;
            }

            .detail-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
                color: rgba(255, 255, 255, 0.4);
            }

            .detail-chips {
                display: flex;
                flex-wrap: wrap;
                gap: 4px;
            }

            .mini-chip {
                font-size: 11px;
                padding: 2px 8px;
                border-radius: 10px;

                &.chip-position {
                    background: color-mix(in srgb, var(--entity-color-positions, #388e3c) 15%, transparent);
                    color: var(--entity-color-positions, #388e3c);
                }
                &.chip-hat {
                    background: color-mix(in srgb, var(--entity-color-hats, #1093ad) 15%, transparent);
                    color: var(--entity-color-hats, #1093ad);
                }
            }

            .invitee-message {
                margin-top: 6px;
                padding-left: 26px;
                font-size: 12px;
                color: rgba(255, 255, 255, 0.5);
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .w-100 {
                width: 100%;
            }

            /* ─── Email Status Indicators ─── */

            .email-status {
                margin-top: -8px;
                margin-bottom: 4px;
            }

            .status-badge {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                padding: 4px 12px;
                border-radius: 16px;
                font-size: 12px;
                font-weight: 400;

                mat-icon {
                    font-size: 16px;
                    width: 16px;
                    height: 16px;
                }
            }

            .status-existing {
                background: rgba(76, 175, 80, 0.15);
                color: #81c784;
                border: 1px solid rgba(76, 175, 80, 0.3);
            }

            .status-new {
                background: rgba(33, 150, 243, 0.15);
                color: #64b5f6;
                border: 1px solid rgba(33, 150, 243, 0.3);
            }

            .status-org-member {
                background: rgba(255, 152, 0, 0.15);
                color: #ffb74d;
                border: 1px solid rgba(255, 152, 0, 0.3);
            }

            .status-pending {
                background: rgba(255, 193, 7, 0.15);
                color: #ffd54f;
                border: 1px solid rgba(255, 193, 7, 0.3);
            }

            .status-checking {
                background: rgba(255, 255, 255, 0.06);
                color: rgba(255, 255, 255, 0.5);
                border: 1px solid rgba(255, 255, 255, 0.1);
            }

            .status-icon-ok {
                color: #81c784;
            }

            .status-icon-warn {
                color: #ffb74d;
            }

            .status-icon-new {
                color: #64b5f6;
            }

            .spin-icon {
                animation: spin 1s linear infinite;
            }

            @keyframes spin {
                from {
                    transform: rotate(0deg);
                }
                to {
                    transform: rotate(360deg);
                }
            }

            /* ═══════════════ Tab Group ═══════════════ */

            .invite-tabs {
                flex: 1;
                display: flex;
                flex-direction: column;
                overflow: hidden;
                min-height: 0;
            }

            ::ng-deep .invite-tabs .mat-mdc-tab-body-wrapper {
                flex: 1;
                overflow: hidden;
            }

            ::ng-deep .invite-tabs .mat-mdc-tab-body {
                display: flex;
                flex-direction: column;
                overflow: hidden !important;
            }

            ::ng-deep .invite-tabs .mat-mdc-tab-body-content {
                display: flex;
                flex-direction: column;
                overflow: hidden !important;
                flex: 1;
            }

            .tab-icon {
                margin-right: 6px;
                font-size: 18px;
                width: 18px;
                height: 18px;
            }

            .tab-badge {
                margin-left: 6px;
                background: rgba(100, 181, 246, 0.2);
                color: #64b5f6;
                border-radius: 10px;
                padding: 1px 8px;
                font-size: 11px;
                font-weight: 500;
            }

            /* ═══════════════ Sent Invitations Tab ═══════════════ */

            .sent-invitations-container {
                padding: 16px 24px;
                overflow-y: auto;
                flex: 1;
                min-height: 0;
            }

            .loading-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 16px;
                padding: 48px 0;
            }

            .loading-text {
                color: rgba(255, 255, 255, 0.5);
                font-size: 14px;
            }

            /* ─── Drift Summary (Phase B.7) ─── */

            .drift-summary {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 8px;
                padding: 10px 14px;
                margin-bottom: 12px;
                background: rgba(100, 181, 246, 0.06);
                border: 1px solid rgba(100, 181, 246, 0.18);
                border-radius: 8px;
                font-size: 13px;
                color: rgba(255, 255, 255, 0.7);
            }
            .drift-total {
                font-weight: 600;
                color: #ffffff;
            }
            .drift-arrow {
                color: rgba(255, 255, 255, 0.35);
                font-weight: 500;
            }
            .drift-dot {
                color: rgba(255, 255, 255, 0.25);
            }
            .drift-segment strong {
                font-weight: 600;
            }
            .drift-accepted strong {
                color: #81c784;
            }
            .drift-pending strong {
                color: #ffd54f;
            }
            .drift-declined strong {
                color: #e57373;
            }
            .drift-other strong {
                color: #bdbdbd;
            }

            /* ─── Filter Row ─── */

            .filter-row {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 16px;
                flex-wrap: wrap;
            }

            .filter-label {
                font-size: 13px;
                color: rgba(255, 255, 255, 0.5);
                margin-right: 4px;
            }

            .filter-chip {
                font-size: 12px !important;
                min-width: auto !important;
                padding: 0 12px !important;
                height: 28px !important;
                line-height: 28px !important;
                border-radius: 14px !important;
                border-color: rgba(255, 255, 255, 0.15) !important;
                color: rgba(255, 255, 255, 0.6) !important;
            }

            .filter-chip.filter-active {
                border-color: #64b5f6 !important;
                color: #64b5f6 !important;
                background: rgba(100, 181, 246, 0.1) !important;
            }

            .filter-pending.filter-active {
                border-color: #ffd54f !important;
                color: #ffd54f !important;
                background: rgba(255, 213, 79, 0.1) !important;
            }
            .filter-accepted.filter-active {
                border-color: #81c784 !important;
                color: #81c784 !important;
                background: rgba(129, 199, 132, 0.1) !important;
            }
            .filter-declined.filter-active {
                border-color: #e57373 !important;
                color: #e57373 !important;
                background: rgba(229, 115, 115, 0.1) !important;
            }
            .filter-expired-btn.filter-active {
                border-color: #ffb74d !important;
                color: #ffb74d !important;
                background: rgba(255, 183, 77, 0.1) !important;
            }
            .filter-cancelled.filter-active {
                border-color: #bdbdbd !important;
                color: #bdbdbd !important;
                background: rgba(189, 189, 189, 0.1) !important;
            }

            .refresh-btn {
                margin-left: auto;
                color: rgba(255, 255, 255, 0.5);
            }

            /* ─── Empty State ─── */

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 48px 0;
                color: rgba(255, 255, 255, 0.4);
            }

            .empty-icon {
                font-size: 48px;
                width: 48px;
                height: 48px;
                margin-bottom: 12px;
                opacity: 0.5;
            }

            /* ─── Sent Invitation Cards ─── */

            .sent-list {
                display: flex;
                flex-direction: column;
                gap: 10px;
            }

            .sent-card {
                background: rgba(255, 255, 255, 0.03);
                border-radius: 8px;
                padding: 14px 16px;
                border: 1px solid rgba(255, 255, 255, 0.08);
                transition: border-color 0.2s;
            }

            .sent-card:hover {
                border-color: rgba(255, 255, 255, 0.15);
            }

            .sent-card-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 12px;
            }

            .sent-email-row {
                display: flex;
                align-items: center;
                gap: 8px;
                flex: 1;
                min-width: 0;
            }

            .sent-email-icon {
                color: rgba(255, 255, 255, 0.5);
                font-size: 20px;
                width: 20px;
                height: 20px;
                flex-shrink: 0;
            }

            .sent-email {
                font-weight: 500;
                font-size: 14px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .invitation-type-chip {
                font-size: 10px;
                padding: 2px 8px;
                border-radius: 10px;
                flex-shrink: 0;
            }

            .type-existing {
                background: rgba(76, 175, 80, 0.15);
                color: #81c784;
            }

            .type-new {
                background: rgba(33, 150, 243, 0.15);
                color: #64b5f6;
            }

            /* ─── Status Chips ─── */

            .sent-status-chip {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                padding: 3px 10px;
                border-radius: 12px;
                font-size: 12px;
                font-weight: 500;
                flex-shrink: 0;
            }

            .status-chip-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
            }

            .status-chip-pending {
                background: rgba(255, 193, 7, 0.15);
                color: #ffd54f;
            }

            .status-chip-accepted {
                background: rgba(76, 175, 80, 0.15);
                color: #81c784;
            }

            .status-chip-declined {
                background: rgba(229, 115, 115, 0.15);
                color: #e57373;
            }

            .status-chip-expired {
                background: rgba(255, 152, 0, 0.15);
                color: #ffb74d;
            }

            .status-chip-cancelled {
                background: rgba(189, 189, 189, 0.15);
                color: #bdbdbd;
            }

            /* ─── Sent Card Details ─── */

            .sent-card-details {
                margin-top: 8px;
                padding-left: 28px;
            }

            .sent-detail {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 4px;
                font-size: 12px;
                color: rgba(255, 255, 255, 0.6);
            }

            /* ─── Action Buttons ─── */

            .sent-card-actions {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-top: 10px;
                padding-left: 28px;
            }

            .action-resend {
                font-size: 12px !important;
                color: #64b5f6 !important;
                border-color: rgba(100, 181, 246, 0.3) !important;

                mat-icon {
                    font-size: 16px;
                    width: 16px;
                    height: 16px;
                    margin-right: 4px;
                }
            }

            .action-revoke {
                font-size: 12px !important;
                color: #e57373 !important;
                border-color: rgba(229, 115, 115, 0.3) !important;

                mat-icon {
                    font-size: 16px;
                    width: 16px;
                    height: 16px;
                    margin-right: 4px;
                }
            }

            .action-delete {
                font-size: 12px !important;
                color: #bdbdbd !important;
                border-color: rgba(189, 189, 189, 0.3) !important;

                mat-icon {
                    font-size: 16px;
                    width: 16px;
                    height: 16px;
                    margin-right: 4px;
                }
            }

            .action-spinner {
                margin-left: 4px;
            }

            /* Email delivery status indicators */
            .email-delivery-status {
                margin-top: 4px;
            }

            .email-sent {
                color: #66bb6a !important;
            }

            .email-failed {
                color: #ef5350 !important;
            }

            .email-sent-text {
                color: #66bb6a;
                font-size: 12px;
            }

            .email-failed-text {
                color: #ef5350;
                font-size: 12px;
                font-weight: 500;
            }

            /* ═══════════════ Light Theme Defaults ═══════════════ */

            .dialog-title {
                border-bottom-color: rgba(0, 0, 0, 0.12);
                .title-icon {
                    color: #1976d2;
                }
            }

            .dialog-footer {
                border-top-color: rgba(0, 0, 0, 0.12);
            }

            .section {
                background: rgba(0, 0, 0, 0.02);
                border-color: rgba(0, 0, 0, 0.08);
            }

            .section-header {
                color: rgba(0, 0, 0, 0.87);
                mat-icon {
                    color: rgba(0, 0, 0, 0.54);
                }
            }

            .optional-badge {
                color: rgba(0, 0, 0, 0.45);
                background: rgba(0, 0, 0, 0.05);
            }

            .section-hint {
                color: rgba(0, 0, 0, 0.54);
            }

            .group-label {
                color: rgba(0, 0, 0, 0.54);
            }

            .chip-position {
                background: color-mix(in srgb, var(--entity-color-positions, #388e3c) 12%, transparent);
                color: var(--entity-color-positions, #388e3c);
                border-color: color-mix(in srgb, var(--entity-color-positions, #388e3c) 30%, transparent);
            }

            .chip-hat {
                background: color-mix(in srgb, var(--entity-color-hats, #1093ad) 12%, transparent);
                color: var(--entity-color-hats, #1093ad);
                border-color: color-mix(in srgb, var(--entity-color-hats, #1093ad) 30%, transparent);
            }

            .invitee-card {
                background: rgba(0, 0, 0, 0.02);
                border-color: rgba(0, 0, 0, 0.08);
            }

            .invitee-email mat-icon {
                color: rgba(0, 0, 0, 0.54);
            }

            .detail-icon {
                color: rgba(0, 0, 0, 0.45);
            }

            .mini-chip {
                &.chip-position {
                    background: color-mix(in srgb, var(--entity-color-positions, #388e3c) 10%, transparent);
                    color: var(--entity-color-positions, #388e3c);
                }
                &.chip-hat {
                    background: color-mix(in srgb, var(--entity-color-hats, #1093ad) 10%, transparent);
                    color: var(--entity-color-hats, #1093ad);
                }
            }

            .invitee-message {
                color: rgba(0, 0, 0, 0.54);
            }

            .status-existing {
                color: #2e7d32;
            }
            .status-new {
                color: #1565c0;
            }
            .status-org-member {
                color: #e65100;
            }
            .status-pending {
                color: #f57f17;
            }
            .status-checking {
                color: rgba(0, 0, 0, 0.5);
                background: rgba(0, 0, 0, 0.04);
                border-color: rgba(0, 0, 0, 0.1);
            }
            .status-icon-ok {
                color: #2e7d32;
            }
            .status-icon-warn {
                color: #e65100;
            }
            .status-icon-new {
                color: #1565c0;
            }

            .tab-badge {
                background: rgba(25, 118, 210, 0.15);
                color: #1565c0;
            }

            .loading-text {
                color: rgba(0, 0, 0, 0.54);
            }

            .filter-label {
                color: rgba(0, 0, 0, 0.54);
            }
            .filter-chip {
                border-color: rgba(0, 0, 0, 0.2) !important;
                color: rgba(0, 0, 0, 0.6) !important;
            }
            .filter-chip.filter-active {
                border-color: #1976d2 !important;
                color: #1976d2 !important;
                background: rgba(25, 118, 210, 0.08) !important;
            }
            .filter-pending.filter-active {
                border-color: #f57f17 !important;
                color: #f57f17 !important;
                background: rgba(255, 193, 7, 0.08) !important;
            }
            .filter-accepted.filter-active {
                border-color: #2e7d32 !important;
                color: #2e7d32 !important;
                background: rgba(76, 175, 80, 0.08) !important;
            }
            .filter-declined.filter-active {
                border-color: #c62828 !important;
                color: #c62828 !important;
                background: rgba(229, 115, 115, 0.08) !important;
            }
            .filter-expired-btn.filter-active {
                border-color: #e65100 !important;
                color: #e65100 !important;
                background: rgba(255, 152, 0, 0.08) !important;
            }
            .filter-cancelled.filter-active {
                border-color: #616161 !important;
                color: #616161 !important;
                background: rgba(158, 158, 158, 0.08) !important;
            }
            .refresh-btn {
                color: rgba(0, 0, 0, 0.54);
            }

            .empty-state {
                color: rgba(0, 0, 0, 0.45);
            }

            .sent-card {
                background: rgba(0, 0, 0, 0.02);
                border-color: rgba(0, 0, 0, 0.08);
            }
            .sent-card:hover {
                border-color: rgba(0, 0, 0, 0.2);
            }
            .sent-email-icon {
                color: rgba(0, 0, 0, 0.54);
            }
            .type-existing {
                color: #2e7d32;
            }
            .type-new {
                color: #1565c0;
            }

            .status-chip-pending {
                color: #f57f17;
            }
            .status-chip-accepted {
                color: #2e7d32;
            }
            .status-chip-declined {
                color: #c62828;
            }
            .status-chip-expired {
                color: #e65100;
            }
            .status-chip-cancelled {
                color: #616161;
            }

            .sent-detail {
                color: rgba(0, 0, 0, 0.6);
            }

            .action-resend {
                color: #1976d2 !important;
                border-color: rgba(25, 118, 210, 0.3) !important;
            }
            .action-revoke {
                color: #c62828 !important;
                border-color: rgba(198, 40, 40, 0.3) !important;
            }
            .action-delete {
                color: #616161 !important;
                border-color: rgba(97, 97, 97, 0.3) !important;
            }

            /* ═══════════════ Dark Theme Overrides ═══════════════ */

            :host-context(.dark-theme) {
                .dialog-title {
                    border-bottom-color: rgba(255, 255, 255, 0.1);
                    .title-icon {
                        color: #64b5f6;
                    }
                }

                .dialog-footer {
                    border-top-color: rgba(255, 255, 255, 0.1);
                }

                .section {
                    background: rgba(255, 255, 255, 0.04);
                    border-color: rgba(255, 255, 255, 0.08);
                }

                .section-header {
                    color: rgba(255, 255, 255, 0.87);
                    mat-icon {
                        color: rgba(255, 255, 255, 0.54);
                    }
                }

                .optional-badge {
                    color: rgba(255, 255, 255, 0.4);
                    background: rgba(255, 255, 255, 0.06);
                }

                .section-hint {
                    color: rgba(255, 255, 255, 0.5);
                }
                .group-label {
                    color: rgba(255, 255, 255, 0.5);
                }

                .chip-position {
                    background: color-mix(in srgb, var(--entity-color-positions, #388e3c) 20%, transparent);
                    color: var(--entity-color-positions, #388e3c);
                    border-color: color-mix(in srgb, var(--entity-color-positions, #388e3c) 30%, transparent);
                }
                .chip-hat {
                    background: color-mix(in srgb, var(--entity-color-hats, #1093ad) 20%, transparent);
                    color: var(--entity-color-hats, #1093ad);
                    border-color: color-mix(in srgb, var(--entity-color-hats, #1093ad) 30%, transparent);
                }

                .invitee-card {
                    background: rgba(255, 255, 255, 0.03);
                    border-color: rgba(255, 255, 255, 0.08);
                }
                .invitee-email mat-icon {
                    color: rgba(255, 255, 255, 0.5);
                }
                .detail-icon {
                    color: rgba(255, 255, 255, 0.4);
                }

                .mini-chip {
                    &.chip-position {
                        background: color-mix(in srgb, var(--entity-color-positions, #388e3c) 15%, transparent);
                        color: var(--entity-color-positions, #388e3c);
                    }
                    &.chip-hat {
                        background: color-mix(in srgb, var(--entity-color-hats, #1093ad) 15%, transparent);
                        color: var(--entity-color-hats, #1093ad);
                    }
                }

                .invitee-message {
                    color: rgba(255, 255, 255, 0.5);
                }

                .status-existing {
                    color: #81c784;
                }
                .status-new {
                    color: #64b5f6;
                }
                .status-org-member {
                    color: #ffb74d;
                }
                .status-pending {
                    color: #ffd54f;
                }
                .status-checking {
                    color: rgba(255, 255, 255, 0.5);
                    background: rgba(255, 255, 255, 0.06);
                    border-color: rgba(255, 255, 255, 0.1);
                }
                .status-icon-ok {
                    color: #81c784;
                }
                .status-icon-warn {
                    color: #ffb74d;
                }
                .status-icon-new {
                    color: #64b5f6;
                }

                .tab-badge {
                    background: rgba(100, 181, 246, 0.2);
                    color: #64b5f6;
                }

                .loading-text {
                    color: rgba(255, 255, 255, 0.5);
                }

                .filter-label {
                    color: rgba(255, 255, 255, 0.5);
                }
                .filter-chip {
                    border-color: rgba(255, 255, 255, 0.15) !important;
                    color: rgba(255, 255, 255, 0.6) !important;
                }
                .filter-chip.filter-active {
                    border-color: #64b5f6 !important;
                    color: #64b5f6 !important;
                    background: rgba(100, 181, 246, 0.1) !important;
                }
                .filter-pending.filter-active {
                    border-color: #ffd54f !important;
                    color: #ffd54f !important;
                    background: rgba(255, 213, 79, 0.1) !important;
                }
                .filter-accepted.filter-active {
                    border-color: #81c784 !important;
                    color: #81c784 !important;
                    background: rgba(129, 199, 132, 0.1) !important;
                }
                .filter-declined.filter-active {
                    border-color: #e57373 !important;
                    color: #e57373 !important;
                    background: rgba(229, 115, 115, 0.1) !important;
                }
                .filter-expired-btn.filter-active {
                    border-color: #ffb74d !important;
                    color: #ffb74d !important;
                    background: rgba(255, 183, 77, 0.1) !important;
                }
                .filter-cancelled.filter-active {
                    border-color: #bdbdbd !important;
                    color: #bdbdbd !important;
                    background: rgba(189, 189, 189, 0.1) !important;
                }
                .refresh-btn {
                    color: rgba(255, 255, 255, 0.5);
                }

                .empty-state {
                    color: rgba(255, 255, 255, 0.4);
                }

                .sent-card {
                    background: rgba(255, 255, 255, 0.03);
                    border-color: rgba(255, 255, 255, 0.08);
                }
                .sent-card:hover {
                    border-color: rgba(255, 255, 255, 0.15);
                }
                .sent-email-icon {
                    color: rgba(255, 255, 255, 0.5);
                }
                .type-existing {
                    color: #81c784;
                }
                .type-new {
                    color: #64b5f6;
                }

                .status-chip-pending {
                    color: #ffd54f;
                }
                .status-chip-accepted {
                    color: #81c784;
                }
                .status-chip-declined {
                    color: #e57373;
                }
                .status-chip-expired {
                    color: #ffb74d;
                }
                .status-chip-cancelled {
                    color: #bdbdbd;
                }

                .sent-detail {
                    color: rgba(255, 255, 255, 0.6);
                }

                .action-resend {
                    color: #64b5f6 !important;
                    border-color: rgba(100, 181, 246, 0.3) !important;
                }
                .action-revoke {
                    color: #e57373 !important;
                    border-color: rgba(229, 115, 115, 0.3) !important;
                }
                .action-delete {
                    color: #bdbdbd !important;
                    border-color: rgba(189, 189, 189, 0.3) !important;
                }
            }

            /* ═══════════════ Entity Color Overrides ═══════════════ */
            /* These must come after dark-theme block so cascade order wins on equal specificity */

            .section-header mat-icon.icon-positions {
                color: var(--entity-color-positions, #388e3c) !important;
            }
            .section-header mat-icon.icon-hats {
                color: var(--entity-color-hats, #1093ad) !important;
            }

            .group-label.group-positions mat-icon {
                color: var(--entity-color-positions, #388e3c) !important;
            }
            .group-label.group-hats mat-icon {
                color: var(--entity-color-hats, #1093ad) !important;
            }

            .detail-icon.icon-positions {
                color: var(--entity-color-positions, #388e3c) !important;
            }
            .detail-icon.icon-hats {
                color: var(--entity-color-hats, #1093ad) !important;
            }
        `,
    ],
})
export class OrgInviteUserDialogComponent implements OnInit, OnDestroy {
    // ── Injected Services ──
    private snackbar = inject(MatSnackBar);
    private matDialog = inject(MatDialog);
    private cdr = inject(ChangeDetectorRef);

    // ── Tab index ──
    public activeTabIndex = 0;

    // ── Form Controls ──
    public emailForm = new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email]),
    });
    public messageControl = new FormControl<string>('', { nonNullable: true });

    // ── Selected Items (from positions-with-hats browser) ──
    public selectedPositions: DisplayValue[] = [];
    public selectedHats: DisplayValue[] = [];
    private selectedPositionKeys: string[] = [];
    private selectedHatKeys: string[] = [];

    // ── Name lookup maps ──
    private positionNameMap = new Map<string, string>();
    private hatNameMap = new Map<string, string>();

    // ── Invitation List (new invitations to send) ──
    public invitees: InviteeEntry[] = [];

    // ── Sent Invitations (tracking) ──
    public sentInvitations: InvitationDocument[] = [];
    public filteredInvitations: InvitationDocument[] = [];
    public sentLoading = false;
    public sentStatusFilter: string | undefined;
    private sentInvitationsLoaded = false;
    public actionLoadingKeys = new Set<string>();

    // ── Email Status Check ──
    public emailStatus: CheckEmailStatusResponse | null = null;
    public emailChecking = false;
    private lastCheckedEmail = '';
    private emailCheckSub: import('rxjs').Subscription | null = null;

    private destroy$ = new Subject<void>();

    constructor(
        public dialogRef: MatDialogRef<OrgInviteUserDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: InviteUserDialogData
    ) {
        // Full-bleed on phones (DESIGN.md §Sheets, OE-160); phone-scoped in
        // global-styles.scss, desktop untouched.
        this.dialogRef.addPanelClass('ng-fullbleed-dialog');
    }

    // ================================================================
    // Lifecycle
    // ================================================================

    public ngOnInit(): void {
        this.buildNameMaps();
    }

    public ngOnDestroy(): void {
        this.emailCheckSub?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ================================================================
    // Getters
    // ================================================================

    public get orgName(): string {
        return this.data.organization?.name || '';
    }

    // ================================================================
    // Email Status Check
    // ================================================================

    /**
     * Called on email input blur. Triggers an email status check
     * if the email is valid and hasn't been checked already.
     */
    public onEmailBlur(): void {
        const email = this.emailForm.controls['email'].value?.toLowerCase().trim();
        if (!email || this.emailForm.controls['email'].hasError('email')) {
            this.emailStatus = null;
            return;
        }
        if (email === this.lastCheckedEmail) return;

        this.checkEmailStatusFn(email);
    }

    /**
     * Perform the email status check.
     * First does a quick client-side org check, then calls the backend if a callback is provided.
     */
    private checkEmailStatusFn(email: string): void {
        // Cancel any in-flight check
        this.emailCheckSub?.unsubscribe();
        this.lastCheckedEmail = email;

        // Quick client-side org check
        const existsInOrg = this.isEmailAlreadyOnOrg(email);

        // If no backend callback provided, show client-side result only
        if (!this.data.checkEmailStatus) {
            this.emailStatus = {
                existsInAuth: false,
                existsInOrg,
            };
            this.cdr.detectChanges();
            return;
        }

        // Call backend for full check
        this.emailChecking = true;
        this.emailStatus = null;
        this.cdr.detectChanges();

        this.emailCheckSub = this.data.checkEmailStatus(email, this.data.organization.key).subscribe({
            next: (result) => {
                // Merge backend result with client-side org check (client side is authoritative for org)
                this.emailStatus = {
                    ...result,
                    existsInOrg: existsInOrg || result.existsInOrg,
                };
                this.emailChecking = false;
                this.cdr.detectChanges();
            },
            error: () => {
                // On error, fall back to client-side org check only
                this.emailStatus = {
                    existsInAuth: false,
                    existsInOrg,
                };
                this.emailChecking = false;
                this.cdr.detectChanges();
            },
        });
    }

    // ================================================================
    // Build Name Maps
    // ================================================================

    private buildNameMaps(): void {
        const org = this.data.organization;

        // Build position name map from orgParts
        if (org?.orgParts) {
            const flattenPart = (part: Record<string, unknown>, parentPath = '') => {
                const name = (part['name'] || part['mainTitle'] || '') as string;
                const displayPath = parentPath ? `${parentPath} > ${name}` : name;
                const key = (part['partKey'] || part['key'] || '') as string;
                if (key) this.positionNameMap.set(key, displayPath);

                const childTypes = ['sections', 'units', 'posts', 'children'];
                for (const childType of childTypes) {
                    if (part[childType] && Array.isArray(part[childType])) {
                        for (const child of part[childType] as Record<string, unknown>[]) {
                            flattenPart(child, displayPath);
                        }
                    }
                }
            };
            for (const orgPart of org.orgParts) {
                flattenPart(orgPart as unknown as Record<string, unknown>);
            }
        }

        // Build hat name map from orgHats
        if (org?.orgHats?.hatsKeys) {
            for (const hatKey of org.orgHats.hatsKeys) {
                const hat = org.orgHats[hatKey];
                if (hat && !hat.isSoftDeleted) {
                    this.hatNameMap.set(hatKey, hat.name || hatKey);
                }
            }
        }
    }

    // ================================================================
    // Position & Hat Selector Dialog
    // ================================================================

    /**
     * Opens the positions-with-hats tree selector.
     * Pre-selects any currently selected positions and hats.
     */
    public openPositionHatSelector(): void {
        const preSelectedKeys = [...this.selectedPositionKeys, ...this.selectedHatKeys];

        const dialogRef = this.matDialog.open(OrgItemSelectorDialogComponent, {
            width: '70vw',
            height: '80vh',
            maxWidth: '900px',
            maxHeight: '800px',
            panelClass: 'item-selector-panel',
            data: {
                organization: this.data.organization,
                type: 'positions-with-hats',
                selectedKeys: preSelectedKeys,
            },
        });

        dialogRef.afterClosed().subscribe((result: ItemSelectorDialogResult | null) => {
            if (!result) return;

            // Position selections come in selectedKeys/selectedItems
            this.selectedPositionKeys = result.selectedKeys || [];
            this.selectedPositions = result.selectedItems || [];

            // Hat selections come via permissionSelections._hatKeys/_hatNames
            const hatKeys: string[] = result.permissionSelections?.['_hatKeys'] || [];
            const hatNames: string[] = result.permissionSelections?.['_hatNames'] || [];
            this.selectedHatKeys = hatKeys;
            this.selectedHats = hatKeys.map((key: string, idx: number) => ({
                display: hatNames[idx] || key,
                value: key,
            }));

            this.cdr.detectChanges();
        });
    }

    // ================================================================
    // Remove Selections
    // ================================================================

    public removePosition(key: string): void {
        this.selectedPositionKeys = this.selectedPositionKeys.filter((k) => k !== key);
        this.selectedPositions = this.selectedPositions.filter((p) => p.value !== key);
    }

    public removeHat(key: string): void {
        this.selectedHatKeys = this.selectedHatKeys.filter((k) => k !== key);
        this.selectedHats = this.selectedHats.filter((h) => h.value !== key);
    }

    // ================================================================
    // Invitee List Management
    // ================================================================

    /**
     * Add the current email (with positions/hats) to the invitation list.
     */
    public addUserToList(): void {
        const email = this.emailForm.controls['email'].value?.toLowerCase().trim();
        if (!email) return;

        // Validate email format
        if (this.emailForm.invalid) {
            this.snackbar.open('Please enter a valid email address.', 'Close', { duration: 3000 });
            return;
        }

        // Check for duplicates in the list
        if (this.invitees.some((inv) => inv.email === email)) {
            this.snackbar.open(`${email} is already in the invitation list.`, 'Close', { duration: 3000 });
            return;
        }

        // Check if user is already in the organization
        if (this.isEmailAlreadyOnOrg(email)) {
            this.snackbar.open(`${email} is already a member of this organization.`, 'Close', { duration: 5000 });
            return;
        }

        // Add to list with current position/hat selections
        this.invitees.push({
            email,
            positionKeys: [...this.selectedPositionKeys],
            hatKeys: [...this.selectedHatKeys],
            message: this.messageControl.value || undefined,
        });

        // Clear only the email form, keep positions/hats for next entry
        this.emailForm.controls['email'].setValue('');
        this.emailForm.controls['email'].markAsUntouched();
        this.emailStatus = null;
        this.lastCheckedEmail = '';

        this.snackbar.open(`${email} added to invitation list.`, 'Close', { duration: 2000 });
    }

    /**
     * Remove an invitee from the list.
     */
    public removeFromList(index: number): void {
        this.invitees.splice(index, 1);
    }

    /**
     * Check if an email is already associated with a user in the organization.
     */
    private isEmailAlreadyOnOrg(email: string): boolean {
        const userKeys = this.data.organization?.orgUsers?.userKeys || [];
        return userKeys.some((key) => {
            const orgUser = this.data.organization?.orgUsers?.[key];
            return orgUser?.email?.toLowerCase() === email;
        });
    }

    // ================================================================
    // Display Helpers
    // ================================================================

    /** Get position name by partKey */
    public getPositionName(partKey: string): string {
        return this.positionNameMap.get(partKey) || partKey;
    }

    /** Get hat name by hatKey */
    public getHatName(hatKey: string): string {
        return this.hatNameMap.get(hatKey) || hatKey;
    }

    // ================================================================
    // Send Invitations
    // ================================================================

    /**
     * Send invitations and close the dialog.
     * Returns the InviteUserDialogResult to the caller.
     */
    public sendInvitations(): void {
        if (this.invitees.length === 0) return;

        const emailCount = this.invitees.length;
        const message =
            emailCount > 2
                ? `Sending invitations to ${this.invitees[0].email} and ${emailCount - 1} ${emailCount - 1 > 1 ? 'others' : 'other'}.`
                : `Sending invitations to ${this.invitees.map((i) => i.email).join(', ')}.`;

        this.snackbar.open(message, 'Close', { duration: 5000 });

        const result: InviteUserDialogResult = {
            invitations: this.invitees.map((inv) => ({
                email: inv.email,
                positionKeys: inv.positionKeys.length > 0 ? inv.positionKeys : undefined,
                hatKeys: inv.hatKeys.length > 0 ? inv.hatKeys : undefined,
                message: inv.message,
            })),
        };

        this.dialogRef.close(result);
    }

    // ================================================================
    // Sent Invitations Tab
    // ================================================================

    /**
     * Called when switching tabs. Lazy-loads sent invitations on first visit.
     */
    public onTabChange(index: number): void {
        this.activeTabIndex = index;
        if (index === 1 && !this.sentInvitationsLoaded) {
            this.loadSentInvitations();
        }
    }

    /**
     * Load sent invitations from the backend.
     */
    public loadSentInvitations(): void {
        if (!this.data.getOrgInvitations) return;

        this.sentLoading = true;
        this.cdr.detectChanges();

        this.data
            .getOrgInvitations(this.data.organization.key)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.sentInvitations = response.invitations || [];
                    this.applyFilter();
                    this.sentLoading = false;
                    this.sentInvitationsLoaded = true;
                    this.cdr.detectChanges();
                },
                error: (err) => {
                    console.error('Failed to load sent invitations:', err);
                    this.sentLoading = false;
                    this.snackbar.open('Failed to load invitations.', 'Close', { duration: 3000 });
                    this.cdr.detectChanges();
                },
            });
    }

    /**
     * Filter sent invitations by status.
     */
    public filterSentInvitations(status: string | undefined): void {
        this.sentStatusFilter = status;
        this.applyFilter();
    }

    private applyFilter(): void {
        if (!this.sentStatusFilter) {
            this.filteredInvitations = [...this.sentInvitations];
        } else {
            this.filteredInvitations = this.sentInvitations.filter((inv) => inv.status === this.sentStatusFilter);
        }
    }

    /**
     * Count invitations by status.
     */
    public countByStatus(status: string): number {
        return this.sentInvitations.filter((inv) => inv.status === status).length;
    }

    /**
     * Get the icon for an invitation status.
     */
    public getStatusIcon(status: string): string {
        switch (status) {
            case 'pending':
                return 'hourglass_empty';
            case 'accepted':
                return 'check_circle';
            case 'declined':
                return 'cancel';
            case 'expired':
                return 'timer_off';
            case 'cancelled':
                return 'block';
            default:
                return 'help';
        }
    }

    /**
     * Format a Firestore timestamp for display.
     */
    public formatTimestamp(ts: unknown): string {
        if (!ts) return 'N/A';
        try {
            // Handle Firestore Timestamp objects (with _seconds or seconds)
            const tsObj = ts as Record<string, unknown>;
            if (tsObj['_seconds'] || tsObj['seconds']) {
                const seconds = (tsObj['_seconds'] || tsObj['seconds']) as number;
                return new Date(seconds * 1000).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                });
            }
            // Fallback: try as Date
            const date = new Date(ts as string | number);
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                });
            }
        } catch {
            /* ignore */
        }
        return 'N/A';
    }

    /**
     * TrackBy function for the invitation list.
     */
    public trackByKey(_index: number, inv: InvitationDocument): string {
        return inv.key;
    }

    /**
     * Resend a pending invitation.
     */
    public resendInvitationAction(inv: InvitationDocument): void {
        if (!this.data.resendInvitation) return;

        this.actionLoadingKeys.add(inv.key);
        this.cdr.detectChanges();

        this.data
            .resendInvitation(inv.key)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.actionLoadingKeys.delete(inv.key);
                    if (response.success) {
                        this.snackbar.open(response.message, 'Close', { duration: 4000 });
                        // Reload to get updated timestamp
                        this.loadSentInvitations();
                    } else {
                        this.snackbar.open(response.message, 'Close', { duration: 5000 });
                    }
                    this.cdr.detectChanges();
                },
                error: (err) => {
                    this.actionLoadingKeys.delete(inv.key);
                    console.error('Failed to resend invitation:', err);
                    this.snackbar.open('Failed to resend invitation.', 'Close', { duration: 3000 });
                    this.cdr.detectChanges();
                },
            });
    }

    /**
     * Revoke (cancel) a pending invitation.
     */
    public revokeInvitationAction(inv: InvitationDocument): void {
        if (!this.data.revokeInvitation) return;

        this.actionLoadingKeys.add(inv.key);
        this.cdr.detectChanges();

        this.data
            .revokeInvitation(inv.key)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.actionLoadingKeys.delete(inv.key);
                    if (response.success) {
                        this.snackbar.open(response.message, 'Close', { duration: 4000 });
                        // Reload to reflect the updated status
                        this.loadSentInvitations();
                    } else {
                        this.snackbar.open(response.message, 'Close', { duration: 5000 });
                    }
                    this.cdr.detectChanges();
                },
                error: (err) => {
                    this.actionLoadingKeys.delete(inv.key);
                    console.error('Failed to revoke invitation:', err);
                    this.snackbar.open('Failed to revoke invitation.', 'Close', { duration: 3000 });
                    this.cdr.detectChanges();
                },
            });
    }

    /**
     * Permanently delete an invitation record (non-pending only).
     */
    public deleteInvitationAction(inv: InvitationDocument): void {
        if (!this.data.deleteInvitation) return;

        this.actionLoadingKeys.add(inv.key);
        this.cdr.detectChanges();

        this.data
            .deleteInvitation(inv.key)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.actionLoadingKeys.delete(inv.key);
                    if (response.success) {
                        this.snackbar.open(response.message, 'Close', { duration: 4000 });
                        // Remove locally instead of reloading to preserve scroll position
                        this.sentInvitations = this.sentInvitations.filter((i) => i.key !== inv.key);
                        this.applyFilter();
                    } else {
                        this.snackbar.open(response.message, 'Close', { duration: 5000 });
                    }
                    this.cdr.detectChanges();
                },
                error: (err) => {
                    this.actionLoadingKeys.delete(inv.key);
                    console.error('Failed to delete invitation:', err);
                    this.snackbar.open('Failed to delete invitation.', 'Close', { duration: 3000 });
                    this.cdr.detectChanges();
                },
            });
    }
}
